import { useEffect } from 'react';
import type { SeoData } from '../siteConfig';
import { absoluteSeoImage } from '../seo';
import { siteConfig } from '../siteConfig';

const setMeta = (selector: string, attribute: string, value: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    const match = selector.match(/^meta\[(name|property)="(.+)"\]$/);
    if (!match) return;
    element.setAttribute(match[1], match[2]);
    document.head.append(element);
  }
  element.setAttribute(attribute, value);
};

export const MetaManager = ({ seo }: { seo: SeoData }) => {
  useEffect(() => {
    document.title = seo.title;

    setMeta('meta[name="description"]', 'content', seo.description);
    setMeta('meta[name="robots"]', 'content', seo.robots || 'index, follow, max-image-preview:large');
    setMeta('meta[property="og:type"]', 'content', seo.type);
    setMeta('meta[property="og:locale"]', 'content', siteConfig.locale);
    setMeta('meta[property="og:site_name"]', 'content', siteConfig.name);
    setMeta('meta[property="og:url"]', 'content', new URL(seo.canonicalPath, siteConfig.baseUrl).toString());
    setMeta('meta[property="og:title"]', 'content', seo.title);
    setMeta('meta[property="og:description"]', 'content', seo.description);
    setMeta('meta[property="og:image"]', 'content', absoluteSeoImage(seo));
    setMeta('meta[property="og:image:secure_url"]', 'content', absoluteSeoImage(seo));
    setMeta('meta[property="og:image:type"]', 'content', 'image/webp');
    setMeta('meta[property="og:image:alt"]', 'content', seo.imageAlt);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', seo.title);
    setMeta('meta[name="twitter:description"]', 'content', seo.description);
    setMeta('meta[name="twitter:image"]', 'content', absoluteSeoImage(seo));
    setMeta('meta[name="twitter:image:alt"]', 'content', seo.imageAlt);

    if (seo.type === 'article') {
      setMeta('meta[property="article:published_time"]', 'content', seo.publishedAt || '');
      setMeta('meta[property="article:modified_time"]', 'content', seo.updatedAt || '');
      setMeta('meta[property="article:section"]', 'content', seo.articleSection || '');
    }

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.append(canonical);
    }
    canonical.href = new URL(seo.canonicalPath, siteConfig.baseUrl).toString();

    document.head.querySelectorAll('[data-autosens-jsonld]').forEach((node) => node.remove());
    seo.structuredData.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.autosensJsonld = '';
      script.text = JSON.stringify(data);
      document.head.append(script);
    });
  }, [seo]);

  return null;
};
